import React, { useEffect, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";

import { showNotification } from "../../../helpers/ShowNotification";
import { getAllBrands } from "../../../redux/CarsManagement/Brands/brandsSlice";
import { getAllBrandTypes } from "../../../redux/CarsManagement/BrandTypes/brandTypesSlice";
import { addCar } from "../../../redux/CarsManagement/Cars/carsSlice";
import { colorOptions, manufacturingYears } from "../../../helpers/Utilities";
import LoadingOverlay from "../../../helpers/LoadingOverlay/LoadingOverlay";
import StatusSelect from "../../../components/CustomSelect/StatusSelect";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomInput from "../../../components/CustomInput/CustomInput";

const mapPriceItem = (item) => {
  const priceListNumber =
    item.priceListNumber ??
    item.PriceListNumber ??
    item.id ??
    item.priceId ??
    item.priceCode ??
    item.code;

  const sellingPrice =
    item.sellingPrice ??
    item.SellingPrice ??
    item.price ??
    item.amount;

  const labelParts = [
    sellingPrice != null ? String(sellingPrice) : null,
    item.arDescription ?? item.description ?? item.nameAr,
    item.enDescription ?? item.nameEn,
    priceListNumber != null ? `#${priceListNumber}` : null,
  ].filter(Boolean);

  return {
    value: priceListNumber,
    label: labelParts.join(" - ") || String(priceListNumber ?? ""),
    raw: item,
  };
};

const mergePriceOptions = (prev, next) => {
  const map = new Map([...prev, ...next].map((o) => [o.value, o]));
  return [...map.values()];
};

function AddCar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux
  const { addCarFormValues, isLoading } = useSelector((state) => state.cars);
  const { allBrands, isLoading: brandsLoading } = useSelector((state) => state.brands);
  const { allBrandTypes, isLoading: brandTypesLoading } = useSelector((state) => state.brandTypes);

  // States
  const [image, setImage] = useState(null);

  // Single variation (object or null)
  const [selectedVariation, setSelectedVariation] = useState(null);

  const [variationsLoading, setVariationsLoading] = useState(false);
  const [allLoadedVariations, setAllLoadedVariations] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  // Price list & selected price (single objects)
  const [priceListData, setPriceListData] = useState(null); // { items, loading, hasMore, pageNumber }
  const [selectedPrice, setSelectedPrice] = useState(null); // selected price option
  const [variationFile, setVariationFile] = useState(null); // uploaded Excel file

  // Options
  const manufacturingOptions = manufacturingYears();
  const brandOptions = allBrands.map((brand) => ({ value: brand.id, label: brand.nameAr }));
  const brandTypesOptions = allBrandTypes.map((item) => ({ value: item.id, label: item.nameAr }));

  // Form
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    control,
    watch,
  } = useForm({ defaultValues: addCarFormValues });

  const selectedBrandId = watch("brand_id");
  const selectedBrandTypeId = watch("brandType_id");

  useEffect(() => {
    reset(addCarFormValues);
  }, [addCarFormValues, reset]);

  // Get brands on mount
  useEffect(() => {
    dispatch(getAllBrands());
  }, [dispatch]);

  // Image change
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(URL.createObjectURL(e.target.files[0]));
    }
  };

  // Load brand types
  const handleBrandTypes = async (brandId) => {
    if (!brandId) return;
    await dispatch(getAllBrandTypes(brandId));
  };

  // Reset everything when brand or type changes
  useEffect(() => {
    setSelectedVariation(null);
    setAllLoadedVariations([]);
    setPageNumber(1);
    setHasMore(true);
    setSearchValue("");
    setPriceListData(null);
    setSelectedPrice(null);
    setVariationFile(null);
  }, [selectedBrandId, selectedBrandTypeId]);

  // Fetch price list for a given variation (used only for the single variation)
  const fetchPriceList = useCallback(
    async (modelVariationId, page = 1, append = false) => {
      // Update local loading state
      setPriceListData((prev) => ({
        items: append ? prev?.items ?? [] : [],
        pageNumber: page,
        hasMore: prev?.hasMore ?? false,
        loading: true,
      }));

      try {
        const response = await axios.get("/Cars/get-price-list-by-model-code", {
          params: {
            ModelVariationId: modelVariationId,
            PageNumber: page,
            PageSize: 100,
          },
        });

        const items = response?.data?.data?.items || [];
        const mappedItems = items.map(mapPriceItem);

        setPriceListData((prev) => {
          const existing = append ? prev?.items ?? [] : [];
          return {
            items: mergePriceOptions(existing, mappedItems),
            pageNumber: page,
            hasMore: items.length >= 100,
            loading: false,
          };
        });
      } catch (error) {
        console.log(error);
        setPriceListData((prev) => ({
          items: append ? prev?.items ?? [] : [],
          pageNumber: page,
          hasMore: false,
          loading: false,
        }));
      }
    },
    []
  );

  // Handle single variation change
  const handleVariationChange = (newValue) => {
    // newValue is { value, label } or null
    if (newValue?.value === selectedVariation?.value) return;

    // Reset price & file
    setPriceListData(null);
    setSelectedPrice(null);
    setVariationFile(null);

    setSelectedVariation(newValue);

    // Fetch price list for the new variation
    if (newValue?.value) {
      fetchPriceList(newValue.value, 1, false);
    }
  };

  const handlePriceSelect = (option) => {
    setSelectedPrice(option);
  };

  const handleLoadMorePrices = () => {
    if (!selectedVariation?.value) return;
    if (!priceListData || priceListData.loading || !priceListData.hasMore) return;

    fetchPriceList(selectedVariation.value, priceListData.pageNumber + 1, true);
  };

  // Fetch variations list
  const fetchVariations = useCallback(
    async ({ page = 1, search = "", append = false }) => {
      try {
        const brand = allBrands.find((x) => x.id === selectedBrandId);
        const model = allBrandTypes.find((x) => x.id === selectedBrandTypeId);
        if (!brand || !model) return [];

        setVariationsLoading(true);

        const response = await axios.get(`/Cars/get-all-model-variations`, {
          params: {
            pageNumber: page,
            pageSize: 100,
            companyCode: 10,
            ManufactoryName: brand.nameEn,
            ModelCodeName: model.nameEn,
            Search: search,
          },
        });

        const data = response?.data?.data;
        const items = data?.items || [];
        setHasMore(items.length >= 100);

        const mappedItems = items.map((item) => ({
          value: item.modelVariationId,
          label: item.arDescription,
          enDescription: item.enDescription,
        }));

        if (append) {
          setAllLoadedVariations((prev) => [...prev, ...mappedItems]);
        } else {
          setAllLoadedVariations(mappedItems);
        }

        return mappedItems;
      } catch (error) {
        console.log(error);
        return [];
      } finally {
        setVariationsLoading(false);
      }
    },
    [allBrands, allBrandTypes, selectedBrandId, selectedBrandTypeId]
  );

  // Initial load of variations when brand & type selected
  useEffect(() => {
    if (selectedBrandId && selectedBrandTypeId) {
      setPageNumber(1);
      setHasMore(true);
      fetchVariations({ page: 1, search: "", append: false });
    }
  }, [selectedBrandId, selectedBrandTypeId, fetchVariations]);

  // Async search function
  const loadVariations = async (inputValue) => {
    setSearchValue(inputValue);
    if (!inputValue || inputValue.length < 2) {
      return allLoadedVariations;
    }
    setPageNumber(1);
    const result = await fetchVariations({ page: 1, search: inputValue, append: false });
    return result;
  };

  // Infinite scroll
  const handleMenuScrollToBottom = async () => {
    if (variationsLoading || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    await fetchVariations({
      page: nextPage,
      search: searchValue.length >= 2 ? searchValue : "",
      append: true,
    });
  };

  // File change for the single variation
  const handleVariationFileChange = (file) => {
    setVariationFile(file);
  };

  // Submit
  const handleAddCar = (data) => {
    if (!selectedVariation) {
      showNotification({
        message: "يرجى اختيار موديل واحد على الأقل",
        status: "warning",
      });
      return;
    }

    // Validate that a price is selected
    const raw = selectedPrice?.raw ?? {};
    const sellingPrice =
      raw.sellingPrice ?? raw.SellingPrice ?? raw.price ?? raw.amount;
    if (!selectedPrice || sellingPrice == null) {
      showNotification({
        message: "يرجى اختيار سعر للموديل",
        status: "warning",
      });
      return;
    }

    // Fallback: when text fields are empty, name the car with the selected model variation
    const arName = selectedVariation.label;
    const enName = selectedVariation.enDescription;
    const fieldFallbacks = {
      nameAr: arName,
      nameEn: enName,
      descriptionAr: arName,
      descriptionEn: enName,
      overviewAr: arName,
      overviewEn: enName,
    };
    for (let key in fieldFallbacks) {
      const value = data[key];
      if (value == null || String(value).trim() === "") {
        data[key] = fieldFallbacks[key] ?? "";
      }
    }

    const formData = new FormData();

    // Colors
    const colors = data.colors || [];
    colors.forEach((color, index) => {
      formData.append(`colors[${index}].code`, color);
    });

    // Single model variation
    formData.append("ModelVariation[0].Id", selectedVariation.value);
    formData.append("ModelVariation[0].ArDescription", selectedVariation.label);

    const priceListNumber =
      raw.priceListNumber ?? raw.PriceListNumber ?? selectedPrice.value;
    if (priceListNumber != null) {
      formData.append("ModelVariation[0].PriceListNumber", priceListNumber);
    }
    if (sellingPrice != null) {
      formData.append("ModelVariation[0].SellingPrice", sellingPrice);
    }
    if (variationFile) {
      formData.append("ModelVariation[0].File", variationFile);
    }

    // Other data
    for (let key in data) {
      if (key === "colors" || key === "ModelVariation") continue;
      if (key === "imageProfile") {
        if (data[key]?.[0]) {
          formData.append(key, data[key][0]);
        }
      } else {
        formData.append(key, data[key]);
      }
    }

    dispatch(addCar(formData)).then((res) => {
      if (!res.error) navigate("/Cars");
    });
  };

  return (
    <>
      {/* Breadcrumb */}
      <nav className="uk-position-relative" aria-label="Breadcrumb">
        <ul className="uk-breadcrumb">
          <li>
            <NavLink to="/Cars">السيارات</NavLink>
          </li>
          <li>
            <span aria-current="page">إضافة سيارة</span>
          </li>
        </ul>
      </nav>

      {brandsLoading || brandTypesLoading ? (
        <LoadingOverlay />
      ) : (
        <div className="uk-padding-small uk-background-default uk-border-rounded">
          <form className="uk-grid-small uk-child-width-1-2" uk-grid="">
            {/* Name Ar */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">الإسم بالعربي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="nameAr" error={errors.nameAr} required={false} />
              </div>
            </div> */}

            {/* Name En */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">الإسم بالإنجليزي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="nameEn" error={errors.nameEn} required={false} />
              </div>
            </div> */}

            {/* Description Ar */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">الوصف بالعربي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="descriptionAr" error={errors.descriptionAr} required={false} />
              </div>
            </div> */}

            {/* Description En */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">الوصف بالإنجليزي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="descriptionEn" error={errors.descriptionEn} required={false} />
              </div>
            </div> */}

            {/* Overview Ar */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">نظرة عامة بالعربي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="overviewAr" error={errors.overviewAr} required={false} />
              </div>
            </div> */}

            {/* Overview En */}
            {/* <div>
              <label className="uk-form-label uk-text-bold">نظرة عامة بالإنجليزي</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="overviewEn" error={errors.overviewEn} required={false} />
              </div>
            </div> */}

            {/* Brand */}
            <div>
              <label className="uk-form-label uk-text-bold">المصنع</label>
              <div className="uk-margin-small-top">
                <Controller
                  name="brand_id"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر المصنع"
                      field={field}
                      options={brandOptions}
                      error={errors.brand_id}
                      onChange={(val) => {
                        field.onChange(val);
                        handleBrandTypes(val);
                      }}
                    />
                  )}
                />
              </div>
            </div>

            {/* Brand Type */}
            <div>
              <label className="uk-form-label uk-text-bold">النوع</label>
              <div className="uk-margin-small-top">
                <Controller
                  name="brandType_id"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر النوع"
                      field={field}
                      options={brandTypesOptions}
                      error={errors.brandType_id}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
            </div>

            {/* Model Variation (single) */}
            <div className="uk-width-1-1">
              <label className="uk-form-label uk-text-bold">الموديل</label>
              <div className="uk-margin-small-top">
                <AsyncSelect
                  cacheOptions
                  defaultOptions={allLoadedVariations}
                  loadOptions={loadVariations}
                  value={selectedVariation}
                  onChange={handleVariationChange}
                  isLoading={variationsLoading}
                  placeholder="ابحث عن موديل (حرفين على الأقل)"
                  noOptionsMessage={() => "لا توجد نتائج"}
                  loadingMessage={() => "جاري التحميل..."}
                  isDisabled={!selectedBrandId || !selectedBrandTypeId}
                  onMenuScrollToBottom={handleMenuScrollToBottom}
                  styles={{
                    menuList: (base) => ({ ...base, maxHeight: 300, overflowY: "auto" }),
                  }}
                />
              </div>

              {selectedVariation && (
                <div className="uk-margin-top">
                  <h5 className="uk-text-bold">الموديل المختار</h5>
                  <span className="uk-label">{selectedVariation.label}</span>

                  <div className="uk-margin-top">
                    <h5 className="uk-text-bold">اختيار السعر</h5>
                    <div className="uk-margin-small-top uk-padding-small uk-background-muted uk-border-rounded">
                      <label className="uk-form-label uk-text-bold">{selectedVariation.label}</label>

                      <div className="uk-grid-small uk-child-width-1-2@m" uk-grid="">
                        {/* File Upload */}
                        <div>
                          <label className="uk-form-label">ملف الإكسيل</label>
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            className="uk-input"
                            onChange={(e) => handleVariationFileChange(e.target.files?.[0])}
                          />
                          {variationFile && (
                            <div className="uk-text-success uk-margin-small-top">
                              {variationFile.name}
                            </div>
                          )}
                        </div>

                        {/* Price Select */}
                        <div>
                          <label className="uk-form-label">السعر</label>

                          {priceListData?.loading && (!priceListData.items || priceListData.items.length === 0) ? (
                            <span className="uk-text-muted">
                              <span uk-spinner="ratio: 0.6"></span> جاري تحميل الأسعار...
                            </span>
                          ) : !priceListData?.items?.length ? (
                            <span className="uk-text-muted">لا توجد أسعار متاحة</span>
                          ) : (
                            <>
                              <Select
                                options={priceListData.items}
                                value={selectedPrice}
                                onChange={handlePriceSelect}
                                placeholder="اختر السعر"
                                isLoading={priceListData?.loading}
                              />
                              {priceListData?.hasMore && (
                                <button
                                  type="button"
                                  className="uk-button uk-button-default uk-button-small uk-margin-small-top"
                                  disabled={priceListData.loading}
                                  onClick={handleLoadMorePrices}
                                >
                                  {priceListData.loading ? "جاري التحميل..." : "تحميل المزيد"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order */}
            <div>
              <label className="uk-form-label uk-text-bold">الترتيب</label>
              <div className="uk-margin-small-top">
                <CustomInput control={control} name="order" type="number" error={errors.order} />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="uk-form-label uk-text-bold">الحالة</label>
              <div className="uk-margin-small-top">
                <Controller
                  name="status"
                  control={control}
                  rules={{
                    validate: (v) => typeof v === "boolean" || "هذا الحقل مطلوب",
                  }}
                  render={({ field }) => <StatusSelect field={field} error={errors.status} />}
                />
              </div>
            </div>

            {/* Manufacture Year */}
            <div>
              <label className="uk-form-label uk-text-bold">سنة الصنع</label>
              <div className="uk-margin-small-top">
                <Controller
                  name="manufacture_year"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر السنة"
                      field={field}
                      options={manufacturingOptions}
                      error={errors.manufacture_year}
                    />
                  )}
                />
              </div>
            </div>

            {/* Image */}
            <div>
              <label className="uk-form-label uk-text-bold">الصورة</label>
              <div className="uk-margin-small-top">
                <div uk-form-custom="">
                  <input
                    type="file"
                    name="imageProfile"
                    {...register("imageProfile", { onChange: handleImageChange })}
                  />
                  <button type="button" className="uk-button uk-button-default">
                    اختر صورة
                  </button>
                  {image && (
                    <img src={image} alt="preview" className="uk-height-small uk-margin-small-right" />
                  )}
                </div>
              </div>
            </div>

            {/* Colors */}
            <h4 className="uk-heading-line uk-width-1-1">
              <span>الألوان</span>
            </h4>
            {colorOptions.map((color) => (
              <div key={color.id} className="uk-form-controls">
                <input
                  className="uk-checkbox"
                  type="checkbox"
                  value={color.value}
                  {...register("colors")}
                />
                <span
                  className="uk-badge uk-margin-small-left"
                  style={{ backgroundColor: color.value }}
                ></span>
                {color.label}
              </div>
            ))}
          </form>

          {/* Actions */}
          <div className="uk-margin-top uk-text-left">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleSubmit(handleAddCar)}
              className="uk-button uk-button-secondary uk-margin-small-left"
            >
              {isLoading ? <div uk-spinner=""></div> : "إضافة"}
            </button>
            <NavLink to="/Cars" className="uk-button uk-button-primary">
              إلغاء
            </NavLink>
          </div>
        </div>
      )}
    </>
  );
}

export default AddCar;